import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SmsProviderService {
  private baseUrl = `${environment.apiUrl}/sms-provider`;

  constructor(private http: HttpClient) {}

  getConfig(): Observable<any> {
    return this.http.get(`${this.baseUrl}/config`);
  }

  updateConfig(data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/config`, data);
  }

  switchProvider(targetProvider: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/switch`, { target_provider: targetProvider });
  }

  // RingCentral auth
  authenticateWithJwt(jwtToken: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/auth-jwt`, { jwt_token: jwtToken });
  }

  getAuthUrl(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.baseUrl}/ringcentral/auth-url`);
  }

  exchangeCompanyOAuthCode(code: string, state: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/oauth-callback`, { code, state });
  }

  createWebhook(): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/create-webhook`, {});
  }

  cleanupWebhooks(): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/cleanup-webhooks`, {});
  }

  disconnectRingCentral(): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/disconnect`, {});
  }

  refreshAllTokens(): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/refresh-all-tokens`, {});
  }

  renewWebhooks(): Observable<any> {
    return this.http.post(`${this.baseUrl}/ringcentral/renew-webhooks`, {});
  }

  // RingCentral extensions
  listExtensions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/ringcentral/extensions`);
  }

  // Phone number pool
  listPhoneNumbers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/phone-numbers`);
  }

  addPhoneNumber(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/phone-numbers`, data);
  }

  updatePhoneNumber(id: number, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/phone-numbers/${id}`, data);
  }

  removePhoneNumber(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/phone-numbers/${id}`);
  }

  assignPhoneToUser(id: number, userId: number | null): Observable<any> {
    return this.http.post(`${this.baseUrl}/phone-numbers/${id}/assign`, { user_id: userId });
  }

  // Per-number RingCentral OAuth
  getNumberAuthUrl(phoneNumberId: number): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.baseUrl}/phone-numbers/${phoneNumberId}/rc-auth-url`);
  }

  exchangeNumberOAuthCode(code: string, state: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/phone-numbers/rc-callback`, { code, state });
  }

  disconnectNumber(phoneNumberId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/phone-numbers/${phoneNumberId}/rc-disconnect`, {});
  }

  // Current user's RC number (for profile page)
  getMyRcNumber(): Observable<any> {
    return this.http.get(`${this.baseUrl}/my-rc-number`);
  }

  getMyRcAuthUrl(): Observable<{ url: string; selfConnect?: boolean }> {
    return this.http.get<{ url: string; selfConnect?: boolean }>(`${this.baseUrl}/my-rc-number/auth-url`);
  }

  selfConnectCallback(code: string, state: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/my-rc-number/self-connect-callback`, { code, state });
  }

  disconnectMyRcNumber(): Observable<any> {
    return this.http.post(`${this.baseUrl}/my-rc-number/disconnect`, {});
  }

  dismissRcPrompt(): Observable<any> {
    return this.http.post(`${this.baseUrl}/my-rc-number/dismiss-prompt`, {});
  }
}
